import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

const IMAGE_BASE_URL = 'https://teamsyncai-assets.tor1.cdn.digitaloceanspaces.com/interview-05-26';

const STEP_IMAGES = {
  1: `${IMAGE_BASE_URL}/interview-help-step-01-open-invitation.jpg`,
  2: `${IMAGE_BASE_URL}/interview-help-step-02-watch-intro-video.jpg`,
  3: `${IMAGE_BASE_URL}/interview-help-step-03-enable-camera-mic.jpg`,
  4: `${IMAGE_BASE_URL}/interview-help-step-04-run-system-checks.jpg`,
  5: `${IMAGE_BASE_URL}/interview-help-step-05-answer-with-timer.jpg`,
  6: `${IMAGE_BASE_URL}/interview-help-step-06-record-and-rerecord.png`,
  7: `${IMAGE_BASE_URL}/interview-help-step-07-submit-answer.jpg`,
  8: `${IMAGE_BASE_URL}/interview-help-step-05-answer-with-timer.jpg`,
  9: `${IMAGE_BASE_URL}/interview-help-step-08-finish-interview.jpg`,
};

/**
 * Help Page
 * Provides step-by-step guide for completing video interviews
 */
const Help = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header - Show generic header for help page since sessionData may not exist */}
      <header className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center space-x-2 group">
              <ArrowLeftIcon className="w-5 h-5 text-gray-600 group-hover:text-indigo-600 transition-colors" />
              <span className="text-sm font-medium text-gray-600 group-hover:text-indigo-600 transition-colors">
                Back
              </span>
            </Link>
            <div className="flex items-center space-x-3">
              <QuestionMarkCircleIcon className="w-6 h-6 text-indigo-600" />
              <h1 className="text-lg font-bold text-gray-900">Help & Support</h1>
            </div>
            <div className="w-12" /> {/* Spacer for alignment */}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-12">
          {/* Introduction */}
          <div className="prose prose-sm max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              How to Complete the Video Interview Setup and Questions
            </h2>
            <p className="text-gray-600 mb-6">
              Follow these steps to successfully complete your interview. For a visual walkthrough, 
              see the <a href="https://vimeo.com/1193400006" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 font-medium">video guide</a>.
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-8">
            {/* Step 1 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-600 text-white font-bold text-sm">
                  1
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Start the interview from the invitation email
                </h3>
                <img
                  src={STEP_IMAGES[1]}
                  alt="Step 1 - Start interview from invitation email"
                  className="w-full max-w-2xl rounded-xl border border-gray-200 shadow-sm mb-4"
                  loading="lazy"
                />
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>Open the interview email you received</li>
                  <li>Enter the provided code</li>
                  <li>Click <strong>Start interview</strong> to begin the process</li>
                </ul>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-600 text-white font-bold text-sm">
                  2
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Watch the intro video and continue setup
                </h3>
                <img
                  src={STEP_IMAGES[2]}
                  alt="Step 2 - Watch the intro video"
                  className="w-full max-w-2xl rounded-xl border border-gray-200 shadow-sm mb-4"
                  loading="lazy"
                />
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>If an introduction video appears, watch it first</li>
                  <li>You'll then see the interview questions on screen</li>
                  <li>Click <strong>Continue setup</strong> to move forward</li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-600 text-white font-bold text-sm">
                  3
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Enable your microphone and camera
                </h3>
                <img
                  src={STEP_IMAGES[3]}
                  alt="Step 3 - Enable microphone and camera"
                  className="w-full max-w-2xl rounded-xl border border-gray-200 shadow-sm mb-4"
                  loading="lazy"
                />
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>Make sure both the <strong>microphone</strong> and <strong>camera</strong> are enabled</li>
                  <li>Follow the on-screen prompts and click the required confirmation buttons</li>
                  <li>This is necessary before the interview can start</li>
                </ul>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-600 text-white font-bold text-sm">
                  4
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Run the system checks
                </h3>
                <img
                  src={STEP_IMAGES[4]}
                  alt="Step 4 - Run system checks"
                  className="w-full max-w-2xl rounded-xl border border-gray-200 shadow-sm mb-4"
                  loading="lazy"
                />
                <div className="text-gray-600 mb-3">
                  The platform will check:
                </div>
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>Internet connection</li>
                  <li>Camera</li>
                  <li>Microphone</li>
                </ul>
                <p className="text-gray-600 mt-3">
                  Wait until the checks pass and the <strong>Start now</strong> button turns green.
                </p>
              </div>
            </div>

            {/* Step 5 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-600 text-white font-bold text-sm">
                  5
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Answer each question within the timer
                </h3>
                <img
                  src={STEP_IMAGES[5]}
                  alt="Step 5 - Answer questions within timer"
                  className="w-full max-w-2xl rounded-xl border border-gray-200 shadow-sm mb-4"
                  loading="lazy"
                />
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>The current question appears on screen</li>
                  <li>Use the timer to manage how long you have to respond</li>
                  <li>The interview includes multiple questions, and your progress is shown as you go</li>
                </ul>
              </div>
            </div>

            {/* Step 6 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-600 text-white font-bold text-sm">
                  6
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Record your response and review it if needed
                </h3>
                <img
                  src={STEP_IMAGES[6]}
                  alt="Step 6 - Record and review response"
                  className="w-full max-w-2xl rounded-xl border border-gray-200 shadow-sm mb-4"
                  loading="lazy"
                />
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>Speak your answer into the microphone</li>
                  <li>When finished, click <strong>Stop</strong></li>
                  <li>If you want to try again, click <strong>Re-record</strong> to discard the current response and record a new one</li>
                </ul>
              </div>
            </div>

            {/* Step 7 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-600 text-white font-bold text-sm">
                  7
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Submit the answer to move to the next question
                </h3>
                <img
                  src={STEP_IMAGES[7]}
                  alt="Step 7 - Submit answer"
                  className="w-full max-w-2xl rounded-xl border border-gray-200 shadow-sm mb-4"
                  loading="lazy"
                />
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>If you're happy with your response, click <strong>Submit answer</strong></li>
                  <li>The system will advance to the next question automatically</li>
                  <li>Repeat the record/review/submit process for each question</li>
                </ul>
              </div>
            </div>

            {/* Step 8 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-600 text-white font-bold text-sm">
                  8
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Continue through the remaining questions
                </h3>
                <img
                  src={STEP_IMAGES[8]}
                  alt="Step 8 - Continue remaining questions"
                  className="w-full max-w-2xl rounded-xl border border-gray-200 shadow-sm mb-4"
                  loading="lazy"
                />
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>Answer each prompt one by one</li>
                  <li>Example question types include:
                    <ul className="list-disc list-inside ml-4 mt-2">
                      <li>Tell us about yourself</li>
                      <li>Describe a time you handled a challenge</li>
                      <li>Explain how you communicate important information</li>
                      <li>Describe how you prioritize work</li>
                      <li>Share your experience mentoring others</li>
                    </ul>
                  </li>
                  <li>Keep submitting answers until all questions are complete</li>
                </ul>
              </div>
            </div>

            {/* Step 9 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-600 text-white font-bold text-sm">
                  9
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Finish the interview
                </h3>
                <img
                  src={STEP_IMAGES[9]}
                  alt="Step 9 - Finish interview"
                  className="w-full max-w-2xl rounded-xl border border-gray-200 shadow-sm mb-4"
                  loading="lazy"
                />
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>After the final question is submitted, the interview is complete</li>
                  <li>You'll see the completion state, and there's nothing else you need to do</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Video Guide CTA */}
          <div className="mt-12 p-6 bg-indigo-50 border border-indigo-200 rounded-lg">
            <h3 className="text-lg font-semibold text-indigo-900 mb-2">Want a visual walkthrough?</h3>
            <p className="text-indigo-800 mb-4">
              Watch the full step-by-step video guide to see exactly how to complete your interview.
            </p>
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <iframe
                src="https://player.vimeo.com/video/1193400006?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                title="teamsyncai-interview-video"
                className="absolute inset-0 w-full h-full rounded-lg"
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Help;
